'use client';
import React, { useState, useCallback, useRef, useEffect } from 'react';
import Plot from 'react-plotly.js';
import { SimpleChartPopover } from './SimpleChartPopover';

interface PlotlyBarChartProps {
  data: {
    x: (string | number)[];
    y: number[];
    color?: string;
    name?: string;
    labels?: string[];
    text?: string[];
  };
  height?: number;
  title?: string;
  unit?: string;
  showLegend?: boolean;
  bargap?: number;
  showValuesOnBars?: boolean;
}

export const PlotlyBarChart: React.FC<PlotlyBarChartProps> = ({
  data,
  height = 300,
  title = '',
  unit = '',
  showLegend = false,
  bargap = 0.7,
  showValuesOnBars = false
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState<number | undefined>(undefined);

  // Use ResizeObserver to handle zoom and resize
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);
    return () => resizeObserver.disconnect();
  }, []);
  // Popover state
  const [popoverData, setPopoverData] = useState<{
    isVisible: boolean;
    position: { x: number; y: number };
    data: {
      label: string;
      value: number;
      color: string;
      name: string;
    } | null;
  }>({
    isVisible: false,
    position: { x: 0, y: 0 },
    data: null,
  });

  // Handle hover events
  const handleHover = useCallback((eventData: any) => {
    if (!eventData || !eventData.points || eventData.points.length === 0) {
      setPopoverData((prev) => ({ ...prev, isVisible: false }));
      return;
    }

    try {
      const point = eventData.points[0];
      const xLabel = point.x;
      const yValue = point.y;
      
      // Get mouse position
      const mouseX = eventData.event?.clientX || 0;
      const mouseY = eventData.event?.clientY || 0;

      setPopoverData({
        isVisible: true,
        position: { x: mouseX, y: mouseY },
        data: {
          label: String(xLabel),  // Just the label like "Jan 01"
          value: yValue,
          color: data.color || '#7071F3',
          name: data.name || 'Count'
        }
      });
    } catch (error) {
      setPopoverData((prev) => ({ ...prev, isVisible: false }));
    }
  }, [data, unit]);

  const handleUnhover = useCallback(() => {
    setPopoverData((prev) => ({ ...prev, isVisible: false }));
  }, []);

  // Generate text labels for bars if enabled
  const barText = showValuesOnBars
    ? (data.text || data.y.map(v => Math.round(v).toString()))
    : undefined;

  const plotData = [{
    x: data.x,
    y: data.y,
    type: 'bar' as const,
    marker: {
      color: data.color || '#7071F3'
    },
    name: data.name || '',
    hoverinfo: 'none' as const, // Disable default tooltip since we use custom popover
    text: barText,
    textposition: showValuesOnBars ? 'outside' as const : undefined,
    textfont: showValuesOnBars ? { size: 10, color: '#6b7280' } : undefined,
    cliponaxis: false
  }];

  const layout = {
    height,
    width: containerWidth,
    autosize: true,
    margin: { t: 10, r: 5, b: 25, l: 20 },
    title: title ? {
      text: title,
      font: { size: 14 }
    } : undefined,
    xaxis: {
      showgrid: false,
      showline: true,
      linecolor: '#e5e7eb',
      zeroline: false,
      showticklabels: true,
      automargin: true,
      tickfont: {
        size: 11,
        color: '#6b7280'
      }
    },
    yaxis: {
      showgrid: true,
      gridcolor: '#e5e7eb',
      showline: false,
      zeroline: false,
      showticklabels: true,
      tickfont: {
        size: 11,
        color: '#6b7280'
      },
      automargin: true,
      tickmode: 'auto' as const,
      nticks: 5,
      range: [0, Math.max(...data.y) * (showValuesOnBars ? 1.2 : 1.1)] // Extra space for text labels
    },
    showlegend: showLegend,
    paper_bgcolor: 'white',
    plot_bgcolor: 'white',
    bargap,
    hovermode: 'closest' as const
  };

  return (
    <div ref={containerRef} className="relative w-full overflow-hidden">
      <Plot
        data={plotData}
        layout={layout}
        config={{
          displayModeBar: false,
          responsive: true
        }}
        style={{ width: '100%', height: '100%' }}
        onHover={handleHover as any}
        onUnhover={handleUnhover as any}
      />
      
      {/* Custom Popover */}
      <SimpleChartPopover
        isVisible={popoverData.isVisible}
        position={popoverData.position}
        data={popoverData.data}
      />
    </div>
  );
};